import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import axios, { endPoints } from 'api/request';
import { AwsCloudOnboardingCatalogDefinitionId } from 'utils/catalogDefinitionId';
import { CssTextField } from 'pages/reference-implementation/components/select';
import { InputLabel } from 'pages/reference-implementation/components/input-label';
import { RIStyles } from 'pages/reference-implementation/Style';
import { formAwsAccConfigFromOrderDetails } from 'pages/reference-implementation/provisioning/RI003AWS/utils';

const AwsCloudOnboardOrderId = ({ value, onChange, onPopulate, onClear, isRequired }) => {
    const [helperText, setHelperText] = React.useState('');
    const [isValidating, setIsValidating] = React.useState(false);
    const [isValid, setIsValid] = React.useState(false);

    const handleSearch = async () => {
        if (!value) return;
        if (!/^DEVX\d{6}$/.test(value)) {
            setHelperText('Order ID must start with DEVX followed by 6 digits (e.g., DEVX123456)');
            setIsValid(false);
            return;
        }
        setIsValidating(true);
        setHelperText('');
        try {
            const { data: orderData } = await axios.get(`${endPoints.order.orderDetailsV2}/${value}`);
            if (!orderData) {
                setHelperText('Order ID not found');
                setIsValid(false);
                return;
            }
            const isCorrectCatalog = orderData?.catalogServices?.some((item) => AwsCloudOnboardingCatalogDefinitionId.includes(item?.catalogServiceCode));
            if (!isCorrectCatalog) {
                setHelperText('Order ID must be related to AWS Cloud Onboarding catalog (AWSCLOUDONBOARD).');
                setIsValid(false);
                return;
            }
            const currentStatus = (orderData?.currentStatus?.status || '').toUpperCase();
            if (currentStatus !== 'COMPLETED') {
                setHelperText('Order must be in completed state.');
                setIsValid(false);
                return;
            }
            const prePopulated = formAwsAccConfigFromOrderDetails(orderData);
            onPopulate(prePopulated);
            setIsValid(true);
            setHelperText('');
        } catch (err) {
            setHelperText(err.response?.status === 404 ? 'Order ID not found' : 'Error validating order ID. Please try again.');
            setIsValid(false);
        } finally {
            setIsValidating(false);
        }
    };

    const handleClear = () => {
        onChange('awsCloudOnboardingOrderId', '');
        setHelperText('');
        setIsValid(false);
        if (onClear) onClear();
    };

    return (
        <CssTextField
            size="small"
            sx={{ width: (theme) => theme.spacing('100%') }}
            label={<InputLabel label="AWS Cloud Onboard Order ID" isRequired />}
            value={value || ''}
            onChange={(e) => {
                const newVal = e.target.value?.trim()?.toUpperCase();
                onChange('awsCloudOnboardingOrderId', newVal);
                setHelperText('');
                setIsValid(false);
                if (!newVal && onClear) onClear();
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            error={(isRequired && !value) || (!!helperText && !isValid)}
            helperText={helperText}
            placeholder="DEVX123456"
            InputProps={{
                endAdornment: (
                    <>
                        {value && (
                            <IconButton aria-label="clear" color="inherit" size="small" onClick={handleClear}>
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        )}
                        <IconButton onClick={handleSearch} data-testid="search-btn">
                            {isValidating ? <CircularProgress size={16} /> : <SearchIcon sx={RIStyles?.form?.searchIcon} />}
                        </IconButton>
                    </>
                ),
            }}
        />
    );
};

AwsCloudOnboardOrderId.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onPopulate: PropTypes.func.isRequired,
    onClear: PropTypes.func,
    isRequired: PropTypes.bool,
};

AwsCloudOnboardOrderId.defaultProps = {
    value: '',
    onClear: undefined,
    isRequired: false,
};

export default AwsCloudOnboardOrderId;
